import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { SupportModal } from "../../widgets/support"
import { ReviewModal, ReviewForm, Stars } from "../../widgets/review"
import { UserModal } from "../../widgets/user"
import { toast } from "../../widgets/common"

/* Shared */
import { TYPES, STATUSES, typeOf, statusOf } from "../../widgets/support/shared"
import { ago, initialsOf, avatarColorOf } from "../../widgets/user/shared"
import { organizationIcon } from "../../widgets/organization/shared"
import { plural } from "../../widgets/category/shared"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { list, status } from "../../api/Support"


/* The states the list is cut by — "open" is everything not resolved yet */
const FILTERS = [
    { key: "open", text: "Открытые" },
    { key: "new", text: "Новые" },
    { key: "progress", text: "В работе" },
    { key: "done", text: "Решённые" },
    { key: "all", text: "Все" }
]

/* The empty filters */
const INITIAL = { search: "", type: "", status: "open", sort: "new", organization: null }


/* Page Supports — the complaints and the messages: the clients complain about the points and the
   reviews and write to the support, the masters write from Bookify Pro. The requests of the
   landing are leads and live on the page Leads. Every message is a case: new → in work → resolved */
class Supports extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            limit: 30,
            page: 1,
            statistics: null,
            types: [],
            attention: [],
            waiting: null,
            ...INITIAL
        }

        this._support = createRef()
        this._review = createRef()
        this._form = createRef()
        this._user = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(1, true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, type, status, sort, organization } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        list({ page, search, type, status, sort, organizationID: organization ? organization.id : 0 }).then(response => {
            if (response && response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    limit: response.data.limit,
                    statistics: response.data.statistics,
                    types: response.data.types,
                    attention: response.data.attention
                })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    refilter = fields => {
        this.setState(fields, () => this.load(1, true))
    }

    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.load(1, true), text.length === 0 ? 0 : 450)
    }

    toggle = (field, value, empty) => {
        this.refilter({ [field]: this.state[field] === value ? empty : value })
    }

    /* Move a case right from the list, without opening it */
    move = (event, item, next) => {

        event.stopPropagation()

        if (this.state.waiting) {
            return
        }

        this.setState({ waiting: item.key })

        status({ kind: item.kind, id: item.id, status: next }).then(response => {

            this.setState({ waiting: null })

            if (response && response.status === 200) {
                toast.success(next === "done" ? "Отмечено решённым" : "Взято в работу")
                this.load(this.state.page)
            }
            else {
                toast.error("Что-то пошло не так! Попробуйте позднее")
            }
        }).catch(() => {
            this.setState({ waiting: null })
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    openClient = author => {
        this._user.current.open({ id: author.id, name: author.name, image: author.image, phone: author.phone, active: author.active, kind: author.verified ? "app" : "lead" })
    }

    /* The review changed — its complaint and the list read themselves again */
    reviewChanged = () => {
        this._support.current.reload()
        this.load(this.state.page)
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Жалобы и обращения</div>

                <div className="organization-header-tools">
                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.load(1, true) }} className="organization-search service-search">
                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>
                        <input placeholder="Имя, телефон, почта или текст" value={search} onChange={event => this.searchText(event.target.value)} />
                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }
                    </form>
                </div>

            </div>
        )
    }

    /* The inbox — how much is open, the way of the cases and what they are about */
    _summary = () => {

        /* Fields */
        const { statistics, types, type, status } = this.state

        if (!statistics) {
            return null
        }

        const open = statistics.fresh + statistics.progress
        const parts = ["new", "progress", "done"].map(key => ({ key, value: key === "new" ? statistics.fresh : statistics[key], ...STATUSES[key] }))

        return (
            <div className="support-summary">

                <div className="support-inbox">

                    <div className="support-inbox-top">
                        <div>
                            <b>{open.toLocaleString("ru-RU")}</b>
                            <span>{plural(open, "открытое обращение", "открытых обращения", "открытых обращений")} из {statistics.total.toLocaleString("ru-RU")}</span>
                        </div>
                        <div className="support-inbox-chips">
                            <div className="support-inbox-chip"><Icon name="trend" size={13} width={2.2} />+{statistics.week} за 7 дней</div>
                            <div className="support-inbox-chip"><Icon name="check" size={13} width={2.4} />решено за 7 дней: {statistics.resolvedWeek}</div>
                        </div>
                    </div>

                    <div className="support-inbox-bar">
                        {parts.map(part =>
                            <i key={part.key} className={part.accent} style={{ flex: Math.max(part.value, statistics.total > 0 ? 0.6 : 1) }} />
                        )}
                    </div>

                    <div className="support-inbox-legend">
                        {parts.map(part =>
                            <div key={part.key} onClick={() => this.toggle("status", part.key, "open")} className={`${part.accent} ${status === part.key ? "active" : ""}`}>
                                <i />
                                <span>{part.text}</span>
                                <b>{part.value.toLocaleString("ru-RU")}</b>
                            </div>
                        )}
                    </div>

                </div>

                <div className="support-types">
                    {Object.keys(TYPES).map(key => {

                        const meta = TYPES[key]
                        const row = types.find(item => item.type === key)
                        const total = row ? row.total : 0
                        const opened = row ? row.open : 0

                        return (
                            <div key={key} onClick={() => this.toggle("type", key, "")} className={`support-type ${meta.accent} ${type === key ? "active" : ""}`}>
                                <div className="support-type-icon"><Icon name={meta.icon} size={16} width={2} /></div>
                                <div className="support-type-text">
                                    <span>{meta.text}</span>
                                    <b>{opened}<em>/ {total}</em></b>
                                </div>
                                {opened > 0 && <i title="Открытые" />}
                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    /* The points the clients complain about the most */
    _attention = () => {

        /* Fields */
        const { attention } = this.state

        if (attention.length === 0) {
            return null
        }

        return (
            <div className="user-channels-strip review-attention">
                <div className="user-channels-strip-title">На кого жалуются<em>за 90 дней</em></div>
                <div className="review-attention-list">
                    {attention.map(item =>
                        <div key={`${item.id}`} onClick={() => this.refilter({ type: "", status: "all", organization: { id: item.id, name: item.name } })} className="review-attention-item" title="Показать жалобы на точку">
                            <Avatar className="review-attention-avatar" src={item.image ? `${env.mediapoint}organizations/${item.image}` : null} alt={item.name}>
                                {organizationIcon()}
                            </Avatar>
                            <span>{item.name}</span>
                            <b>{item.complaints}</b>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    _toolbar = () => {

        /* Fields */
        const { status, type, sort, count, statistics, organization } = this.state

        return (
            <div className="service-toolbar">

                <div className="service-segment">
                    {FILTERS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ status: item.key })} className={status === item.key ? "active" : ""}>
                            {item.text}
                            {item.key === "new" && statistics && statistics.fresh > 0 && <span className="support-segment-count">{statistics.fresh}</span>}
                        </div>
                    )}
                </div>

                {type &&
                    <div className="service-filter active">
                        <Icon name={typeOf(type).icon} size={15} width={2} />
                        {typeOf(type).text}
                        <div onClick={() => this.refilter({ type: "" })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                {organization &&
                    <div className="service-filter active">
                        <Icon name="organization" size={15} width={2} />
                        {organization.name}
                        <div onClick={() => this.refilter({ organization: null })} className="service-filter-clear"><Icon name="close" size={9} width={2.6} /></div>
                    </div>
                }

                <div className="user-count">{count.toLocaleString("ru-RU")} {plural(count, "обращение", "обращения", "обращений")}</div>

                <div className="service-sort">
                    <Icon name="sort" size={15} width={1.9} />
                    <div onClick={() => this.refilter({ sort: "new" })} className={sort === "new" ? "active" : ""}>Новые</div>
                    <div onClick={() => this.refilter({ sort: "old" })} className={sort === "old" ? "active" : ""}>Старые</div>
                </div>

            </div>
        )
    }

    /* What a complaint is about, in one line */
    _target = item => {

        if (!item.target) {
            return null
        }

        if (item.target.missing) {
            return <div className="support-item-target missing">{item.type === "review" ? "Отзыв уже удалён" : "Точка удалена"}</div>
        }

        if (item.type === "organization") {
            return (
                <div className="support-item-target">
                    <Avatar className="support-item-target-avatar" src={item.target.image ? `${env.mediapoint}organizations/${item.target.image}` : null} alt={item.target.name}>
                        {organizationIcon()}
                    </Avatar>
                    <b>{item.target.name}</b>
                </div>
            )
        }

        return (
            <div className="support-item-target">
                <Stars value={item.target.rate} size={11} />
                <span>{item.target.comment ? `«${item.target.comment}»` : "без текста"}</span>
                <b>{item.target.organization}</b>
            </div>
        )
    }

    /* One message of the feed */
    _item = item => {

        /* Fields */
        const { waiting } = this.state

        const type = typeOf(item.type)
        const state = statusOf(item.status)
        const name = item.name && `${item.name}`.trim().length > 0 ? `${item.name}`.trim() : null
        const image = item.image ? `${env.mediapoint}${item.kind === "member" ? "members" : "users"}/${item.image}` : null

        return (
            <div key={item.key} onClick={() => this._support.current.open(item)} className={`support-item ${item.status} ${type.accent}`}>

                <div className="support-item-avatar-wrap" style={{ "--avatar": avatarColorOf(item.authorID ? item.authorID : item.id) }}>
                    <Avatar className="support-avatar" src={image} alt={name}>
                        {name ? <span>{initialsOf(name)}</span> : <Icon name="user" size={18} width={2} />}
                    </Avatar>
                    <div className={`support-item-kind ${type.accent}`} title={type.text}><Icon name={type.icon} size={10} width={2.6} /></div>
                </div>

                <div className="support-item-body">

                    <div className="support-item-head">
                        <b className={name ? "" : "empty"}>{name ? name : "Без имени"}</b>
                        <div className={`user-badge ${type.accent}`}>{type.text}</div>
                        <span>{item.phone ? item.phone : item.email}</span>
                    </div>

                    <div className="support-item-text">{item.message && `${item.message}`.trim().length > 0 ? `${item.message}`.trim() : "Без текста"}</div>

                    {this._target(item)}

                    {item.note && <div className="support-item-note"><Icon name="edit" size={11} width={2.2} />{item.note}</div>}

                </div>

                <div className="support-item-side">

                    <span title={new Date(item.createdAt).toLocaleString("ru-RU")}>{ago(item.createdAt)}</span>

                    <div className={`user-badge ${state.accent}`}><Icon name={state.icon} size={11} width={2.4} />{state.text}</div>

                    {item.status !== "done" &&
                        <div className="support-item-actions">
                            {item.status === "new" && <div onClick={event => this.move(event, item, "progress")} title="Взять в работу"><Icon name="clock" size={14} width={2.2} /></div>}
                            <div onClick={event => this.move(event, item, "done")} className="done" title="Отметить решённым">
                                {waiting === item.key ? <Icon name="refresh" size={14} width={2.2} /> : <Icon name="check" size={14} width={2.6} />}
                            </div>
                        </div>
                    }

                </div>

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data, count, page, limit } = this.state

        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {this._summary()}
                    {this._attention()}
                    {this._toolbar()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.refilter({ ...INITIAL })} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._summary()}
                {this._attention()}
                {this._toolbar()}

                <div className="support-list">
                    {data.map(item => this._item(item))}
                </div>

                <Pagination size={limit} page={page} total={count} onPageChange={page => { window.scrollTo(0, 0); this.load(page, true) }} />

            </div>
        )
    }

    render = () => {
        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <SupportModal
                    ref={this._support}
                    onChanged={() => this.load(this.state.page)}
                    onReview={review => this._review.current.open(review)}
                    onClient={this.openClient}
                />

                <ReviewModal
                    ref={this._review}
                    onChanged={this.reviewChanged}
                    onEdit={review => this._form.current.open(review)}
                    onClient={review => this.openClient({ id: review.userID, name: review.userName, image: review.userImage, phone: review.userPhone, active: review.userActive, verified: 1 })}
                />

                <ReviewForm ref={this._form} onSaved={() => { this._review.current.reload(); this.reviewChanged() }} />

                <UserModal ref={this._user} onChanged={() => { }} />

            </div>
        )
    }
}

export default Supports
